/**
 * Admin — Brand Studio overview & plan editor.
 *
 * Shows two panels:
 *   1) Plan editor — price, features, highlight, active/inactive. Pricing
 *      is set MANUALLY per currency per institutional decision.
 *   2) Projects list — filtered by status / pending_review with badge.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, Save, Plus, Trash2, Star, BellRing, FileText, Wallet, ExternalLink, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { useToast } from '../../../components/ui/use-toast';
import { Link, useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CURRENCIES = ['USD', 'AED', 'EUR'];

const fmt = (amount, currency) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD', maximumFractionDigits: 0 })
    .format(Number(amount) || 0);

const PlanEditor = ({ plan, onSave, onDelete }) => {
  const [draft, setDraft] = useState(() => structuredClone(plan));
  const [saving, setSaving] = useState(false);

  const updatePrice = (currency, amount) => {
    setDraft((p) => {
      const idx = p.prices.findIndex((x) => x.currency === currency);
      const next = [...p.prices];
      if (idx >= 0) next[idx] = { ...next[idx], amount: Number(amount) };
      else next.push({ currency, amount: Number(amount) });
      return { ...p, prices: next };
    });
  };
  const updateFeature = (i, key, val) => {
    setDraft((p) => ({ ...p, features: p.features.map((f, idx) => idx === i ? { ...f, [key]: val } : f) }));
  };
  const addFeature = () => setDraft((p) => ({ ...p, features: [...(p.features || []), { label: '', included: true }] }));
  const rmFeature = (i) => setDraft((p) => ({ ...p, features: p.features.filter((_, idx) => idx !== i) }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(draft);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid={`plan-editor-${plan.slug}`}>
      <CardContent className="pt-5 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-[#2B2D42]">{plan.name}</h3>
              <code className="text-[11px] bg-gray-100 px-1.5 py-0.5 rounded">{plan.slug}</code>
              {plan.highlight && <Star className="w-4 h-4 text-[#FF6B35] fill-[#FF6B35]" />}
            </div>
            <div className="text-[11px] text-gray-500">{plan.billing_period === 'monthly' ? 'monthly' : 'one-time'}</div>
          </div>
          <Badge variant="outline" className={draft.is_active ? 'border-green-300 text-green-700' : 'border-gray-300 text-gray-500'}>
            {draft.is_active ? 'Active' : 'Inactive'}
          </Badge>
        </div>

        <div>
          <label className="text-xs font-medium text-gray-600">Name</label>
          <input
            type="text" value={draft.name || ''} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            className="w-full mt-1 px-3 py-2 border rounded text-sm"
            data-testid={`plan-${plan.slug}-name`}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600">Tagline</label>
          <input
            type="text" value={draft.tagline || ''} onChange={(e) => setDraft({ ...draft, tagline: e.target.value })}
            className="w-full mt-1 px-3 py-2 border rounded text-sm"
          />
        </div>

        <div>
          <label className="text-xs font-medium text-gray-600">Prices (manual per currency)</label>
          <div className="grid grid-cols-3 gap-2 mt-1">
            {CURRENCIES.map((c) => {
              const tier = draft.prices.find((x) => x.currency === c);
              return (
                <div key={c} className="border rounded p-2">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500">{c}</div>
                  <input
                    type="number" step="1" min="0"
                    value={tier?.amount ?? ''}
                    onChange={(e) => updatePrice(c, e.target.value)}
                    className="w-full mt-1 px-2 py-1 border rounded text-sm font-mono"
                    data-testid={`plan-${plan.slug}-price-${c}`}
                  />
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-gray-600">Features</label>
            <button type="button" onClick={addFeature} className="text-[11px] text-[#00B4D8] hover:underline inline-flex items-center gap-1">
              <Plus className="w-3 h-3" /> Add
            </button>
          </div>
          <div className="space-y-1.5 mt-1 max-h-48 overflow-y-auto">
            {(draft.features || []).map((f, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="checkbox" checked={!!f.included}
                  onChange={(e) => updateFeature(i, 'included', e.target.checked)}
                />
                <input
                  type="text" value={f.label || ''} onChange={(e) => updateFeature(i, 'label', e.target.value)}
                  className="flex-1 px-2 py-1 border rounded text-xs"
                />
                <button type="button" onClick={() => rmFeature(i)} className="text-red-600 p-1">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3 pt-2 border-t">
          <label className="flex items-center gap-2 text-xs text-gray-700">
            <input type="checkbox" checked={!!draft.highlight} onChange={(e) => setDraft({ ...draft, highlight: e.target.checked })} />
            Highlight (Most popular)
          </label>
          <label className="flex items-center gap-2 text-xs text-gray-700">
            <input type="checkbox" checked={!!draft.is_active} onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })} />
            Active
          </label>
          <div className="ml-auto flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={saving} className="bg-[#00B4D8] hover:bg-[#0096c7]" data-testid={`plan-${plan.slug}-save`}>
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
            </Button>
            <Button size="sm" variant="outline" onClick={() => onDelete(plan)} className="text-red-600">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const BrandStudioAdmin = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  const [plans, setPlans] = useState([]);
  const [projects, setProjects] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('projects');
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newProj, setNewProj] = useState({ plan_id: '', currency: 'AED', client_id: null, client_name: '', client_email: '' });
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [clientSearchOpen, setClientSearchOpen] = useState(false);
  const [matchedClient, setMatchedClient] = useState(null);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Debounced CRM client lookup: when admin types in client_name we hit
  // /api/brand/clients/search so the operator never duplicates an
  // existing CRM client. 250ms debounce keeps the request volume low.
  useEffect(() => {
    if (matchedClient || !newProj.client_name || newProj.client_name.trim().length < 2) {
      setClientSearchResults([]);
      setClientSearchOpen(false);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const r = await axios.get(
          `${API_URL}/api/brand/clients/search?q=${encodeURIComponent(newProj.client_name.trim())}`,
          { headers }
        );
        setClientSearchResults(r.data || []);
        setClientSearchOpen((r.data || []).length > 0);
      } catch {
        setClientSearchResults([]);
        setClientSearchOpen(false);
      }
    }, 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newProj.client_name, matchedClient]);

  const selectExistingClient = (c) => {
    setMatchedClient(c);
    setNewProj((p) => ({
      ...p,
      client_id: c.id,
      client_name: c.company_name,
      client_email: c.email || p.client_email,
    }));
    setClientSearchOpen(false);
  };

  const clearMatchedClient = () => {
    setMatchedClient(null);
    setNewProj((p) => ({ ...p, client_id: null }));
  };

  const load = async () => {
    setLoading(true);
    try {
      const [p, pr, pc] = await Promise.all([
        axios.get(`${API_URL}/api/brand/plans?include_inactive=true`, { headers }),
        axios.get(`${API_URL}/api/brand/projects`, { headers }),
        axios.get(`${API_URL}/api/brand/projects/pending-review/count`, { headers }),
      ]);
      setPlans(p.data || []);
      setProjects(pr.data || []);
      setPendingCount(pc.data?.count || 0);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Failed to load Brand Studio', description: e.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const handlePlanSave = async (draft) => {
    try {
      await axios.put(`${API_URL}/api/brand/plans/${draft.id}`, draft, { headers });
      toast({ title: 'Plan saved', description: `${draft.name} updated` });
      await load();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Save failed', description: e.response?.data?.detail || e.message });
    }
  };
  const handlePlanDelete = async (plan) => {
    if (!window.confirm(`Delete plan "${plan.name}"? (soft-delete — public page will hide it)`)) return;
    try {
      await axios.delete(`${API_URL}/api/brand/plans/${plan.id}`, { headers });
      toast({ title: 'Plan deleted' });
      await load();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Delete failed', description: e.response?.data?.detail || e.message });
    }
  };

  const provisionInvoice = async (project) => {
    try {
      const r = await axios.post(`${API_URL}/api/brand/projects/${project.id}/provision-invoice`, {}, { headers });
      toast({
        title: r.data.reused ? 'Invoice already exists' : 'Pro-Forma created',
        description: `Invoice ${r.data.invoice.invoice_number}`,
      });
      await load();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Could not provision invoice', description: e.response?.data?.detail || e.message });
    }
  };

  const handleCreateProject = async () => {
    if (!newProj.plan_id) {
      toast({ variant: 'destructive', title: 'Plan required', description: 'Choose a plan tier first.' });
      return;
    }
    setCreating(true);
    try {
      const r = await axios.post(`${API_URL}/api/brand/projects`, newProj, { headers });
      toast({ title: 'Project created', description: `${r.data.project_code} — opening detail…` });
      setShowCreate(false);
      setNewProj({ plan_id: '', currency: 'AED', client_id: null, client_name: '', client_email: '' });
      setMatchedClient(null);
      setClientSearchResults([]);
      setClientSearchOpen(false);
      navigate(`/admin/brand-studio/projects/${r.data.id}`);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Could not create project', description: e.response?.data?.detail || e.message });
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="brand-studio-admin">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#2B2D42]">Brand Studio</h1>
          <p className="text-sm text-gray-500">Plans, projects and review queue.</p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/admin/brand-studio/hosting-manual"
            data-testid="brand-studio-hosting-manual-link"
            className="inline-flex items-center gap-1 text-sm text-[#0F172A] hover:text-[#00B4D8] border px-3 py-1.5 rounded transition-colors"
          >
            <FileText className="w-3.5 h-3.5" /> Hosting Manual
          </Link>
          <a
            href="/brand-studio" target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-sm text-[#00B4D8] hover:underline"
          >
            View public page <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>

      <div className="flex items-center gap-2 border-b">
        <button
          type="button" onClick={() => setTab('projects')}
          className={`px-4 py-2 -mb-px border-b-2 text-sm font-semibold flex items-center gap-2 ${tab === 'projects' ? 'border-[#00B4D8] text-[#023E8A]' : 'border-transparent text-gray-500'}`}
          data-testid="tab-projects"
        >
          <FileText className="w-4 h-4" /> Projects
          <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">{projects.length}</span>
          {pendingCount > 0 && (
            <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#FF6B35] text-white flex items-center gap-0.5">
              <BellRing className="w-2.5 h-2.5" /> {pendingCount}
            </span>
          )}
        </button>
        <button
          type="button" onClick={() => setTab('plans')}
          className={`px-4 py-2 -mb-px border-b-2 text-sm font-semibold ${tab === 'plans' ? 'border-[#00B4D8] text-[#023E8A]' : 'border-transparent text-gray-500'}`}
          data-testid="tab-plans"
        >
          Pricing Plans
          <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">{plans.length}</span>
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#00B4D8]" /></div>
      ) : tab === 'plans' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((p) => (
            <PlanEditor key={p.id} plan={p} onSave={handlePlanSave} onDelete={handlePlanDelete} />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="text-sm text-gray-600">
                {projects.length} project{projects.length === 1 ? '' : 's'}{pendingCount > 0 && ` · ${pendingCount} awaiting review`}
              </div>
              <Button
                size="sm"
                onClick={() => setShowCreate((v) => !v)}
                className="bg-[#00B4D8] hover:bg-[#0096c7]"
                data-testid="new-project-btn"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> New project
              </Button>
            </div>
            {showCreate && (
              <div className="border border-[#00B4D8]/40 bg-[#00B4D8]/5 rounded p-3 mb-4 grid grid-cols-1 md:grid-cols-4 gap-2" data-testid="new-project-form">
                <select
                  value={newProj.plan_id}
                  onChange={(e) => setNewProj((p) => ({ ...p, plan_id: e.target.value }))}
                  className="px-2 py-1.5 border rounded text-sm bg-white"
                  data-testid="new-project-plan"
                >
                  <option value="">Choose plan…</option>
                  {plans.filter((p) => p.is_active).map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <select
                  value={newProj.currency}
                  onChange={(e) => setNewProj((p) => ({ ...p, currency: e.target.value }))}
                  className="px-2 py-1.5 border rounded text-sm bg-white"
                  data-testid="new-project-currency"
                >
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Client name (type to find existing)"
                    value={newProj.client_name}
                    onChange={(e) => {
                      if (matchedClient) clearMatchedClient();
                      setNewProj((p) => ({ ...p, client_name: e.target.value }));
                    }}
                    onFocus={() => clientSearchResults.length > 0 && setClientSearchOpen(true)}
                    onBlur={() => setTimeout(() => setClientSearchOpen(false), 180)}
                    className={`w-full px-2 py-1.5 border rounded text-sm ${matchedClient ? 'border-emerald-500 bg-emerald-50/50 pr-7' : ''}`}
                    data-testid="new-project-client-name"
                  />
                  {matchedClient && (
                    <button
                      type="button"
                      onClick={clearMatchedClient}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 text-emerald-600 hover:text-emerald-800 text-xs"
                      data-testid="clear-matched-client"
                      title="Clear match"
                    >
                      ✕
                    </button>
                  )}
                  {clientSearchOpen && clientSearchResults.length > 0 && (
                    <ul
                      data-testid="client-search-dropdown"
                      className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg max-h-56 overflow-auto text-sm"
                    >
                      {clientSearchResults.map((c) => (
                        <li
                          key={c.id}
                          onMouseDown={(e) => { e.preventDefault(); selectExistingClient(c); }}
                          className="px-3 py-2 cursor-pointer hover:bg-[#00B4D8]/10 border-b border-gray-100 last:border-0"
                          data-testid={`client-search-option-${c.id}`}
                        >
                          <div className="font-medium text-[#0F172A] truncate">{c.company_name}</div>
                          <div className="text-[11px] text-gray-500 truncate">
                            {c.email || '—'}{c.country ? ` · ${c.country}` : ''}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  {matchedClient && (
                    <div className="absolute -bottom-4 left-0 text-[10px] text-emerald-700 font-medium" data-testid="matched-client-badge">
                      ✓ Existing CRM client
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    type="email"
                    placeholder="client@email.com"
                    value={newProj.client_email}
                    onChange={(e) => setNewProj((p) => ({ ...p, client_email: e.target.value }))}
                    className="flex-1 px-2 py-1.5 border rounded text-sm"
                    data-testid="new-project-client-email"
                  />
                  <Button size="sm" onClick={handleCreateProject} disabled={creating} className="bg-[#0F172A] text-white" data-testid="create-project-confirm">
                    {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              </div>
            )}
            {projects.length === 0 ? (
              <div className="text-sm text-gray-500 py-10 text-center bg-gray-50 rounded border border-dashed">
                No brand projects yet. Leads from the public page land here automatically when a contact form is submitted.
              </div>
            ) : (
              <div className="border rounded overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-600">
                    <tr>
                      <th className="px-3 py-2 text-left">Code</th>
                      <th className="px-3 py-2 text-left">Client</th>
                      <th className="px-3 py-2 text-left">Plan</th>
                      <th className="px-3 py-2 text-right">Price</th>
                      <th className="px-3 py-2 text-left">Status</th>
                      <th className="px-3 py-2 text-left">Review</th>
                      <th className="px-3 py-2 text-right" />
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {projects.map((pr) => (
                      <tr key={pr.id} data-testid={`project-row-${pr.id}`}>
                        <td className="px-3 py-2 font-mono text-xs">{pr.project_code || pr.id.slice(0, 8)}</td>
                        <td className="px-3 py-2">{pr.client_name || pr.client_email || '—'}</td>
                        <td className="px-3 py-2">{pr.plan_snapshot?.name || pr.plan_slug}</td>
                        <td className="px-3 py-2 text-right font-semibold">{fmt(pr.price_amount, pr.currency)}</td>
                        <td className="px-3 py-2 capitalize">{(pr.status || '').replace(/_/g, ' ')}</td>
                        <td className="px-3 py-2">
                          {pr.pending_review ? <Badge className="bg-[#FF6B35] text-white">Pending</Badge> : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Link
                              to={`/admin/brand-studio/projects/${pr.id}`}
                              className="text-xs text-[#00B4D8] hover:underline inline-flex items-center gap-1"
                              data-testid={`open-project-${pr.id}`}
                            >
                              Open <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                            {pr.invoice_id ? (
                              <Link to={`/admin/finance/invoices/${pr.invoice_id}`} className="text-xs text-[#00B4D8] hover:underline inline-flex items-center gap-1">
                                <Wallet className="w-3.5 h-3.5" /> Invoice
                              </Link>
                            ) : (
                              <Button size="sm" variant="outline" onClick={() => provisionInvoice(pr)} data-testid={`provision-${pr.id}`}>
                                <Wallet className="w-3.5 h-3.5 mr-1" /> Provision
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BrandStudioAdmin;
